import type { SystemId } from './ids.js';
import type { DomainGroup } from './taxonomy.js';
import type { MetadataProblem, SystemMetadata } from './system-metadata.js';
import { validateSystemMetadata } from './system-metadata.js';

export class DuplicateSystemIdError extends Error {
  constructor(public readonly systemId: string) {
    super(`Duplicate system id: ${systemId}`);
    this.name = 'DuplicateSystemIdError';
  }
}

export class InvalidSystemMetadataError extends Error {
  constructor(public readonly problems: MetadataProblem[]) {
    super(`Invalid system metadata: ${problems.map((p) => `${p.rule} (${p.detail})`).join('; ')}`);
    this.name = 'InvalidSystemMetadataError';
  }
}

/** The navigation contract: a typed directory of every system. */
export interface FeatureRegistry {
  all(): readonly SystemMetadata[];
  byId(id: SystemId): SystemMetadata | undefined;
  byGroup(group: DomainGroup): readonly SystemMetadata[];
  /** Throws on duplicate id or invalid metadata; no partial registration. */
  register(meta: SystemMetadata): void;
}

export class InMemoryFeatureRegistry implements FeatureRegistry {
  private readonly map = new Map<string, SystemMetadata>();

  constructor(initial: readonly SystemMetadata[] = []) {
    for (const m of initial) this.register(m);
  }

  all(): readonly SystemMetadata[] {
    return [...this.map.values()];
  }

  byId(id: SystemId): SystemMetadata | undefined {
    return this.map.get(String(id));
  }

  byGroup(group: DomainGroup): readonly SystemMetadata[] {
    return this.all().filter((m) => m.group === group);
  }

  register(meta: SystemMetadata): void {
    const key = String(meta.id);
    if (this.map.has(key)) throw new DuplicateSystemIdError(key);
    const problems = validateSystemMetadata(meta);
    if (problems.length > 0) throw new InvalidSystemMetadataError(problems);
    this.map.set(key, meta);
  }
}
